import { displayExperience } from '@/utils/deviceMetrics';
import { getActiveTextModeContext, resolveAssessmentMode } from '@/features/text-mode/analytics/text-mode-context';
import { useDangerSignStore } from '@/features/danger-signs/stores/dangerSign.store';
import { useCollectStore } from '@/stores/collect';
import { useEncounterStore } from '@/stores/encounter';
import { useLanguageStore } from '@/stores/language';
import { useMrxStore } from '@/stores/mrx';
import { useRegistrationStore } from '@/stores/registration';
import { usePartnerConfigStore } from '@/stores/partnerConfig';
import { usePreferenceStore } from '@/stores/preference';
import { useTrackStore } from '@/stores/track';
import { checkResponse, getResponse } from '@/utils/panelAccess';
import { encounterStatusFor } from '@/utils/encounterStatus';

export interface EncounterDetails {
  id: { id: string | null };
  encounter: Record<string, unknown>;
  // classifications are spread in by name
  track: Record<string, unknown>;
  // mrx status entries are spread in by name
  status: Record<string, unknown>;
  patient: Record<string, unknown>;
  user: Record<string, unknown>;
  timing: Record<string, unknown>;
  enrichment: Record<string, unknown>;
  release: unknown;
}

export interface EncounterDetailsEntry {
  type: string;
  name: string;
  value: unknown;
}

const age = (x: number) => Math.round(x / 365) || 1

export function buildEncounterDetails(output?: 'object'): EncounterDetails;
export function buildEncounterDetails(output: 'array'): EncounterDetailsEntry[];
export function buildEncounterDetails(output: 'object' | 'array' = 'object') {
    const collectStore = useCollectStore();
    const encounterStore = useEncounterStore();
    const languageStore = useLanguageStore();
    const mrxStore = useMrxStore();
    const registrationStore = useRegistrationStore();
    const partnerConfigStore = usePartnerConfigStore();
    const trackStore = useTrackStore();
    const activeContext = getActiveTextModeContext();

    let obj: any = {}

    obj.id = { id: encounterStore.uuid }
    obj.encounter = {
        id: partnerConfigStore.id,
        name: partnerConfigStore.name,
        code: partnerConfigStore.code,
        status: partnerConfigStore.status,
        experience: displayExperience(),
        mode: resolveAssessmentMode(activeContext),
        env: (window as any)?.thinkmd?.env,
        language: languageStore.selected,
        network: 'ThinkMD'
    }

    let track = trackStore.current
    const trackDetails: Record<string, unknown> = {
        id: track.id,
        name: track.name,
        trackSequence: collectStore.sequences[track.name] || 1,
        globalSequence: collectStore.globalSequence,
        streamlinedSelections: trackStore.streamlinedOptionsDataSave,
        comprehensive: trackStore.comprehensive,
        thresholdSet: trackStore.thresholdSet,
    }
    if (trackStore.initialAssessmentType === 'focused' && trackStore.comprehensive) {
        trackDetails.initialAssessmentType = 'focused'
    }
    obj.track = trackDetails

    trackStore.classifications.forEach((classification: any) => {
        obj.track[classification.name] = classification.value
    })

    let status = mrxStore.status.reduce((a: any, c: any) => {
        a[c.name] = c.value
        return a
    }, {})

    // The encounter-level fact lives only in the store: the assessment state is a per-condition
    // thing and says nothing about how the encounter itself ended.
    const dangerSignStore = useDangerSignStore();
    const stopped = dangerSignStore.stopRequested;
    const encounterStatus = encounterStatusFor({ stopped, dangerSignFound: dangerSignStore.hasFindings });

    obj.status = Object.assign({}, status, {
        triage: mrxStore.triage,
        triage_selected: mrxStore.triageSelected(),
        assessments: mrxStore.assessments().length,
        treatments: mrxStore.treatments().length,
        plans: mrxStore.plans().length,
        ...(encounterStatus ? { encounter_status: encounterStatus } : {}),
        ...(stopped ? { stopped_early: true } : {}),
    })

    obj.patient = {
        uuid: encounterStore.patient_uuid,
        age: age(getResponse(track, 'age', 'days').value),
        ageInDays: getResponse(track, 'age', 'days').value,
        gender: checkResponse(track, 'gender', 'male') ? 'male' : 'female',
        name: getResponse(track, 'patient_name', 'name').value || null,
        patientId: getResponse(track, 'patient_id', 'number').value || null,
        phoneNumber: getResponse(track, 'patient_phone', 'number').value || null,
        birthDate: null
    }

    let userId = usePreferenceStore().getResponse('user_id', 'number').value || null

    obj.user = {
        id: userId,
        phoneNumber: null,
        type: trackStore.settings('audience') === 'chw' ? 'professional' : 'consumer',
        registration_id: registrationStore.registrationId,
        hr_id: registrationStore.hrId,
    }

    obj.timing = {
        start: encounterStore.start,
        end: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        when: partnerConfigStore.submitOnAssessment ? 'assessment' : 'upload'
    }

    obj.enrichment = {
        user_agent: navigator.userAgent,
        ip_address: partnerConfigStore.ipAddress
    }

    obj.release = partnerConfigStore.release

    if (output === 'array') {
        return Object.entries(obj).reduce((acc: EncounterDetailsEntry[], cur) => {
            let key = cur[0]
            let item = cur[1]
            Object.entries(item as object).forEach(x =>
                acc.push({ type: key, name: x[0], value: x[1] })
            )
            return acc
        }, [])
    }
    else {
        return obj as EncounterDetails
    }
}
